import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@/components/ui/tabs";
import { NAMESPACES } from "@/i18n/constants";
import { AvailableTab } from "./available-tab";
import { DisplayedTab } from "./displayed-tab";
import type { PickerStream } from "./picker-stream";
import type { CatalogStatus } from "./use-picker-catalog";

export type PickerTab = "available" | "displayed";

export function StreamPickerPanel({
    plotted,
    addedIds,
    catalog,
    status,
    onAdd,
    onRemove,
    initialTab = "available",
    initialQuery,
}: {
    plotted: readonly PickerStream[];
    addedIds: ReadonlySet<number>;
    catalog: readonly PickerStream[];
    status: CatalogStatus;
    onAdd: (streamId: number) => void;
    onRemove: (streamId: number) => void;
    initialTab?: PickerTab;
    initialQuery?: string;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const [tab, setTab] = useState<PickerTab>(initialTab);

    return (
        // Fixed height: Base UI measures with --available-height at max-content, so min() doesn't cap it.
        <Tabs
            className={"h-[470px] max-h-full w-[360px] max-w-full gap-0"}
            onValueChange={(value) => setTab(value as PickerTab)}
            value={tab}
        >
            <TabsList className={"w-full border-b px-2.5"} variant={"underline"}>
                <TabsTab value={"available"}>{t("available-tab")}</TabsTab>
                <TabsTab value={"displayed"}>
                    {t("displayed-tab")}
                    <Badge className={"min-w-5 rounded-full px-1.5 tabular-nums"} size={"sm"} variant={"secondary"}>
                        {plotted.length}
                    </Badge>
                </TabsTab>
            </TabsList>
            <TabsPanel className={"flex min-h-0 flex-col"} value={"available"}>
                <AvailableTab
                    catalog={catalog}
                    plotted={plotted}
                    status={status}
                    onAdd={onAdd}
                    initialQuery={initialQuery}
                />
            </TabsPanel>
            <TabsPanel className={"min-h-0 overflow-y-auto"} value={"displayed"}>
                <DisplayedTab plotted={plotted} addedIds={addedIds} onRemove={onRemove} />
            </TabsPanel>
        </Tabs>
    );
}
